/**
 * Query keys. One place, so an invalidation in a feature module cannot miss a
 * cache entry because it spelled the key slightly differently.
 *
 * Keys are hierarchical on purpose: TanStack Query matches by prefix, so
 * `invalidateQueries({ queryKey: qk.occurrencesAll(profileId) })` clears every
 * date range for that profile without the caller knowing which ranges are
 * mounted.
 *
 * AFTER ANY DOSE MUTATION invalidate all three of:
 *   qk.occurrencesAll(profileId)   — the dose itself changed
 *   qk.adherenceAll(profileId)     — the percentage moved
 *   qk.medicationsAll(profileId)   — inventory decrements on `taken`
 * `qk.afterDoseMutation(profileId)` returns exactly that list.
 */

export const qk = {
  /* ---- auth ---- */
  session: () => ['session'] as const,

  /* ---- profiles ---- */
  profiles: () => ['profiles'] as const,
  profile: (profileId: string) => ['profile', profileId] as const,
  grants: (profileId: string) => ['grants', profileId] as const,

  /* ---- medications ---- */
  /** Prefix for every medication list of this profile, any archive filter. */
  medicationsAll: (profileId: string) => ['medications', profileId] as const,
  medications: (profileId: string, includeArchived: boolean = false) =>
    ['medications', profileId, includeArchived] as const,
  medication: (medicationId: string) => ['medication', medicationId] as const,
  inventory: (medicationId: string) => ['inventory', medicationId] as const,

  /* ---- doses ---- */
  /** Prefix for every occurrence range of this profile. */
  occurrencesAll: (profileId: string) => ['occurrences', profileId] as const,
  occurrences: (profileId: string, from: string, to: string) =>
    ['occurrences', profileId, from, to] as const,

  /* ---- adherence ---- */
  adherenceAll: (profileId: string) => ['adherence', profileId] as const,
  adherence: (profileId: string, from: string, to: string) =>
    ['adherence', profileId, from, to] as const,

  /**
   * Every key group a dose write invalidates. Spread into invalidateQueries
   * calls rather than remembering the list:
   *
   *   for (const key of qk.afterDoseMutation(profileId)) {
   *     queryClient.invalidateQueries({ queryKey: key });
   *   }
   */
  afterDoseMutation: (profileId: string) =>
    [
      ['occurrences', profileId],
      ['adherence', profileId],
      ['medications', profileId],
    ] as const,
} as const;

export type QueryKeys = typeof qk;
export default qk;
